import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * Renders a username with a small "(deleted)" badge when the original user
 * has been removed and we are showing the snapshot value. Use it anywhere
 * the backend exposes a `*_username` + `*_is_deleted` pair (Incident reporter,
 * responsible person, history edited_by, …).
 *
 *   <UserNameLabel name={item.reporter_name} isDeleted={item.reporter_is_deleted} />
 *
 * Falls back to `—` when no name is available.
 */
const UserNameLabel = ({
  name,
  isDeleted = false,
  variant = 'body2',
  emptyText = '—',
}) => {
  if (!name) {
    return (
      <Typography variant={variant} color="text.disabled">
        {emptyText}
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography
        variant={variant}
        color={isDeleted ? 'text.disabled' : 'text.primary'}
        sx={{
          textDecoration: isDeleted ? 'line-through' : 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </Typography>
      {isDeleted && (
        <Tooltip title="This user has been removed from the system. The name is preserved from when the record was created.">
          <Chip size="small" label="deleted" color="default" variant="outlined" />
        </Tooltip>
      )}
    </Stack>
  );
};

UserNameLabel.propTypes = {
  name: PropTypes.string,
  isDeleted: PropTypes.bool,
  variant: PropTypes.string,
  emptyText: PropTypes.string,
};

export default UserNameLabel;
